import storage from '../../../../../hooks/useStorage/useStorage';
import LangEnum from '../../../../../lang/LangEnum';
import { formatDate } from '../../../../../utils/dates';
import { TranslateType } from '../../../../translation/translationContext.types';
import { OrderHeadersGroupBy } from '../../../products/orderProducts.types';
import { ExportFormat, ExportOrderForm, ExportOrderScope } from '../ExportOrderModal.types';
import { DEFAULT_EXPORT_ORDER } from './exportOrder.consts';

type DefaultFileNameArgs = {
  translate: TranslateType;
  sufix: string;
  customerCommercialName?: string;
  isOffline?: boolean;
};

const MAX_FILENAME_LENGTH = 251;

const sanitizeForFilename = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_\-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

const buildFilenameWithDate = (prefix: string, date: string): string => {
  const maxPrefixLength = Math.max(0, MAX_FILENAME_LENGTH - date.length - 1);
  return `${prefix.slice(0, maxPrefixLength)}_${date}`;
};

export const defaultOrderFilename = ({
  translate,
  sufix,
  customerCommercialName,
  isOffline,
}: DefaultFileNameArgs) => {
  const date = formatDate(new Date());
  const base = translate(LangEnum.ORDER_FILENAME, { sufix, isOffline });
  const sanitizedCustomerName = customerCommercialName
    ? sanitizeForFilename(customerCommercialName)
    : '';
  const customerSufix = sanitizedCustomerName ? `_${sanitizedCustomerName}` : '';

  return buildFilenameWithDate(`${base}${customerSufix}`, date);
};

export const filledModelOrderFilename = (translate: TranslateType): string =>
  translate(LangEnum.ORDER_XLSX_FILLED_MODEL_FILE_NAME, { date: formatDate(new Date()) });

export const defaultMultipleOrdersFilename = (
  translate: TranslateType,
  offline: boolean,
): string => {
  const date = formatDate(new Date());
  const base = translate(LangEnum.ORDERS_FILENAME, { isOffline: offline });
  return buildFilenameWithDate(base, date);
};

export const saveExportOrderConfigs = (values: ExportOrderForm) => {
  const sessionId = storage.getSessionId();
  const next: ExportOrderForm = { ...DEFAULT_EXPORT_ORDER, ...values };

  if (!sessionId) {
    return { previous: null, next };
  }

  const previous = storage.getExportOrderConfig(sessionId) ?? null;

  storage.setExportOrderConfig(sessionId, next);

  return { previous, next };
};

export const loadExportOrderConfigs = (isOffline?: boolean): ExportOrderForm => {
  const sessionId = storage.getSessionId();
  const saved = storage.getExportOrderConfig(sessionId);

  if (saved) {
    return checkDefaultFields(saved);
  }

  return {
    ...DEFAULT_EXPORT_ORDER,
    format: isOffline ? ExportFormat.CSV : ExportFormat.PDF,
  };
};

const checkDefaultFields = (saved: ExportOrderForm): ExportOrderForm => {
  return { ...DEFAULT_EXPORT_ORDER, ...saved };
};

export const exportOrderModalTitle = (exportScope: ExportOrderScope) => {
  return exportScope && exportScope.isSyncExport
    ? LangEnum.ORDER_EXPORT_ORDER
    : LangEnum.ORDER_MASS_ACTIONS_EXPORT_ORDERS_LABEL;
};

export const primaryActionButton = (
  exportScope: ExportOrderScope,
  format: ExportFormat,
): LangEnum => {
  if (format === ExportFormat.CSV) {
    return LangEnum.GLOBAL_EXPORT;
  }
  return exportScope?.isSyncExport
    ? LangEnum.GLOBAL_EXPORT
    : LangEnum.BATCH_INVITE_MODAL_SEND_BY_EMAIL;
};

const enumPrefixSeparator = '::';
export const makeGroupByEnumPrefix = (groupByEnum: string, id: string) =>
  `${groupByEnum}${enumPrefixSeparator}${id}`;
export const parseGroupByEnumPrefix = (value?: string) => {
  if (!value) {
    return null;
  }
  if (!value.includes(enumPrefixSeparator)) {
    return { groupBy: value as OrderHeadersGroupBy };
  }

  const [groupBy, classificationTypeId] = value.split(enumPrefixSeparator);
  return { groupBy: groupBy as OrderHeadersGroupBy, classificationTypeId };
};
